import { cn } from '@/lib/utils'
import { useSwipeToClose } from '@/lib/useSwipeToClose'
import { Maximize2, Minimize2, X } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { t } from '@/lib/i18n'

interface DrawerProps {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  /** Width class for the drawer panel — defaults to ~400px on desktop. */
  widthClass?: string
  /** Width class when the user clicks the Expand button in the header. */
  widthClassExpanded?: string
  /** Optional aria label for the dialog region. */
  label?: string
}

interface DrawerContextValue {
  expanded: boolean
  onToggleExpand: () => void
  /** 7 Oct 2026: plays the exit animation, then calls the caller's onClose. */
  requestClose: () => void
}

// 7 Oct 2026 (Apple polish): the panel leaves the way it came -- it slides
// back out to the right (and the backdrop fades) before it's removed.
const EXIT_MS = 180
const DrawerContext = createContext<DrawerContextValue | null>(null)
// 15 Oct 2026: when two panels are open (e.g. evidence over Zoe's panel),
// Esc closes only the top one.
const openDrawerStack: symbol[] = []

/**
 * Right-side slide-in drawer with backdrop. Closes on Esc and on backdrop
 * click. Uses a portal-less approach: the children render at the bottom of
 * the parent tree, which is fine because the drawer is fixed-position.
 */
export function Drawer({
  open,
  onClose,
  children,
  widthClass = 'w-full sm:w-[480px] md:w-[560px]',
  widthClassExpanded = 'w-full sm:w-[720px] md:w-[960px] lg:w-[1100px]',
  label = t('Drawer'),
}: DrawerProps) {
  const [expanded, setExpanded] = useState(false)
  const [closing, setClosing] = useState(false)
  const closingRef = useRef(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const requestClose = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    setClosing(true)
    window.setTimeout(() => {
      closingRef.current = false
      setClosing(false)
      onCloseRef.current()
    }, EXIT_MS)
  }, [])

  // 7 Oct 2026 (Apple design release 3): swipe right to close on touch screens.
  const asideRef = useRef<HTMLElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  useSwipeToClose(asideRef, { enabled: open, onClose: () => onCloseRef.current(), backdropRef })

  useEffect(() => {
    if (!open) return
    const me = Symbol('drawer')
    openDrawerStack.push(me)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openDrawerStack[openDrawerStack.length - 1] === me) requestClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      const i = openDrawerStack.lastIndexOf(me)
      if (i >= 0) openDrawerStack.splice(i, 1)
    }
  }, [open, requestClose])

  // Reset expanded state when the drawer closes so the next open starts compact.
  useEffect(() => {
    if (!open) setExpanded(false)
  }, [open])

  if (!open) return null

  return (
    <DrawerContext.Provider
      value={{ expanded, onToggleExpand: () => setExpanded((v) => !v), requestClose }}
    >
      <div
        ref={backdropRef}
        className={cn(
          'fixed inset-0 bg-inverse/40 backdrop-blur-[1px] z-40',
          closing ? 'animate-[fadeOut_0.18s_ease-in_forwards]' : 'animate-[fadeIn_0.2s_ease-out]',
        )}
        onClick={requestClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') requestClose()
        }}
        role="button"
        tabIndex={-1}
        aria-label={t('Close drawer backdrop')}
      />
      {/* biome-ignore lint/a11y/useSemanticElements: native <dialog> needs imperative .showModal which clashes with our search-param-driven open state */}
      <aside
        ref={asideRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={cn(
          'fixed top-0 right-0 bottom-0 bg-card shadow-sm border-l border-border flex flex-col overflow-hidden z-50 transition-[width] duration-200',
          closing
            ? 'animate-[slideOutRight_0.18s_ease-in_forwards]'
            : 'animate-[slideInRight_0.2s_ease-out]',
          expanded ? widthClassExpanded : widthClass,
        )}
      >
        {children}
      </aside>
    </DrawerContext.Provider>
  )
}

interface DrawerHeaderProps {
  children: React.ReactNode
  onClose: () => void
}

export function DrawerHeader({ children, onClose }: DrawerHeaderProps) {
  const ctx = useContext(DrawerContext)
  return (
    <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-border-subtle flex-shrink-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1">{children}</div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {ctx ? (
          <button
            type="button"
            onClick={ctx.onToggleExpand}
            className="text-subtle-foreground hover:text-foreground-muted rounded p-1"
            aria-label={ctx.expanded ? t('Collapse drawer') : t('Expand drawer')}
            title={ctx.expanded ? t('Collapse') : t('Expand')}
          >
            {ctx.expanded ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </button>
        ) : null}
        <button
          type="button"
          onClick={ctx ? ctx.requestClose : onClose}
          className="text-subtle-foreground hover:text-foreground-muted rounded p-1"
          aria-label={t('Close')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export function DrawerBody({ children }: { children: React.ReactNode }) {
  return <div className="flex-1 overflow-y-auto">{children}</div>
}
